import React, { useEffect, useState } from "react";
import { KeyRound, Loader2, Mail, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import client, { formatApiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const inputCls = "w-full border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-emerald-500 bg-white min-h-[44px]";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const strong = (p) => p.length >= 8 && /[A-Za-z]/.test(p) && /\d/.test(p);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }) : "—");

function Pw({ value, onChange, testid, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoComplete="new-password" maxLength={100} className={`${inputCls} pe-11`} data-testid={testid} />
      <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? "Hide password" : "Show password"} className="absolute end-0.5 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-slate-500" data-testid={`${testid}-toggle`}>{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
    </div>
  );
}

// Admin-only: change own login email and/or password (current password required; other devices are logged out by the backend).
export default function AdminAccountSettings() {
  const { refreshUser } = useApp();
  const [acct, setAcct] = useState(null);
  const [cur, setCur] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { client.get("/admin/account").then((r) => setAcct(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  if (!acct) return null;
  const emailChanged = email.trim() && email.trim().toLowerCase() !== acct.email;
  const errs = {
    email: email.trim() && !EMAIL_RE.test(email.trim()) ? "Sahi email likhein" : "",
    pw: pw && !strong(pw) ? "Kam se kam 8 characters, ek letter aur ek number" : "",
    pw2: pw2 && pw !== pw2 ? "Dono naye password match nahi kar rahe" : "",
  };
  const ready = cur && (emailChanged || pw) && !errs.email && !errs.pw && !errs.pw2 && (!pw || pw === pw2);
  const save = async () => {
    if (!ready || busy) return;
    if (!window.confirm("Admin login details badalein? Iske baad baaki sab devices se logout ho jayega.")) return;
    setBusy(true);
    try {
      const r = await client.put("/admin/account", { current_password: cur, ...(emailChanged ? { new_email: email.trim() } : {}), ...(pw ? { new_password: pw } : {}) });
      toast.success(r.data.message);
      setCur(""); setEmail(""); setPw(""); setPw2("");
      setAcct((a) => ({ ...a, email: r.data.email, credentials_updated_at: new Date().toISOString() }));
      refreshUser?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const err = (k) => errs[k] && <span className="block mt-1 text-rose-600 font-normal" data-testid={`admin-acct-${k}-error`}>{errs[k]}</span>;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 mb-5" data-testid="admin-account-settings">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Admin Account &amp; Security Settings</div>
      <div className="text-xs text-slate-500 mt-0.5" data-testid="admin-acct-current">Current login: <span className="font-semibold text-slate-800">{acct.email}</span>{acct.credentials_updated_at ? ` · Last change ${fmt(acct.credentials_updated_at)}` : ""}</div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block text-xs font-semibold text-slate-700 sm:col-span-2"><span className="inline-flex items-center gap-1 mb-1"><KeyRound className="w-3.5 h-3.5" /> Current password *</span>
          <Pw value={cur} onChange={setCur} testid="admin-acct-current-pw" placeholder="Abhi wala password" /></label>
        <label className="block text-xs font-semibold text-slate-700 sm:col-span-2"><span className="inline-flex items-center gap-1 mb-1"><Mail className="w-3.5 h-3.5" /> New login email (optional)</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={120} placeholder={acct.email} autoComplete="off" className={inputCls} data-testid="admin-acct-email" />{err("email")}</label>
        <label className="block text-xs font-semibold text-slate-700"><span className="block mb-1">New password (optional)</span><Pw value={pw} onChange={setPw} testid="admin-acct-new-pw" />{err("pw")}</label>
        <label className="block text-xs font-semibold text-slate-700"><span className="block mb-1">Confirm new password</span><Pw value={pw2} onChange={setPw2} testid="admin-acct-new-pw2" />{err("pw2")}</label>
      </div>
      <p className="mt-2 text-[11px] text-slate-500">Save karne ke baad yeh device login rahega; baaki sab phones/browsers se admin logout ho jayega.</p>
      <button type="button" onClick={save} disabled={!ready || busy} className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-sm font-semibold min-h-[40px] disabled:opacity-50" data-testid="admin-acct-save">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Save Login Details</button>
    </div>
  );
}
